import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../Icon';
import { EmojiIcon } from '../EmojiIcon';
import { MemorySymbolV1 } from '../MemorySymbolV1';
import { BLOCKS } from '../../shared/memory-map-data';
import { SIGNS, SIGN_VALUES, cap, getMemoryMap, getUserName } from '../../shared/memory-map-helpers';
import styles from './MemoryBookSlide.css';

const RING_RADIUS = 32;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;
const RING_SHARE = 0.08;

function MemoryBookSlide({ results, spriteVersion }) {
    const map = getMemoryMap(results.userResponses);
    const name = getUserName(results.userResponses);
    const perks = [
        { icon: 'key', title: 'Go where the quiz couldn’t.', text: 'One question at a time, into the rooms you skipped.' },
        { icon: 'cloud-with-lightning', title: 'Say it, finally.', text: `${cap(map.anyHeavy)}: out of your head, onto the page.` },
        { icon: 'mirror', title: 'Meet yourself again.', text: 'The kid at home, the one at school, the one who loved for the first time. One story, finally in order.' },
        { icon: 'sun', title: 'Keep what was good.', text: `${cap(map.anyWarm)}, before the details go.` }
    ];

    return (
        <section className={styles.form}>
            <p className={styles.eyebrow}>Your memory book is ready to open</p>
            <div className={styles.card}>
                <h1 className={styles.heading}>{name ? `${name}’s` : 'Your'} memories</h1>
                <div className={styles.summary}>
                    <div className={styles.ring}>
                        <svg
                            width='72' height='72' viewBox='0 0 72 72'
                            aria-hidden='true'
                        >
                            <circle
                                cx='36' cy='36' r={RING_RADIUS}
                                className={styles.ringTrack}
                            />
                            <circle
                                cx='36'
                                cy='36'
                                r={RING_RADIUS}
                                className={styles.ringFill}
                                strokeDasharray={`${(RING_LENGTH * RING_SHARE).toFixed(1)} ${RING_LENGTH.toFixed(1)}`}
                                transform='rotate(-90 36 36)'
                            />
                        </svg>
                        <span className={styles.total}>{map.total}</span>
                    </div>
                    <p className={styles.note}>{`You stirred up ${map.total} memories in 3 minutes. Thousands more are still in there.`}</p>
                </div>
                {BLOCKS.map((block, index) => (
                    <div className={styles.bookRow} key={block.key}>
                        <span className={styles.symbol}>
                            <MemorySymbolV1
                                type={SIGNS[index]}
                                value={SIGN_VALUES[SIGNS[index]][map.ch[index].state - 1]}
                                size={index === 0 ? 60 : 48}
                            />
                        </span>
                        <span className={styles.rowText}>
                            <span className={styles.rowName}>{block.name}</span>
                            <span className={styles.rowLock}>
                                <Icon name='lockSolid' />
                                {index === map.top ? 'heaviest, ready to open' : 'ready to open'}
                            </span>
                        </span>
                        <span className={styles.rowChapter}>{`Ch. ${index + 1}`}</span>
                    </div>
                ))}
            </div>
            <div className={styles.card}>
                <h2 className={styles.heading}>That was the first 1%. Unlock the rest.</h2>
                <ul className={styles.perks}>
                    {perks.map(perk => (
                        <li className={styles.perk} key={perk.title}>
                            <EmojiIcon name={perk.icon} version={spriteVersion} />
                            <span className={styles.perkText}>
                                <span className={styles.perkTitle}>{perk.title}</span>
                                <span className={styles.perkNote}>{perk.text}</span>
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

MemoryBookSlide.propTypes = {
    results: PropTypes.shape({ userResponses: PropTypes.array }).isRequired,
    spriteVersion: PropTypes.string.isRequired
};

export {
    MemoryBookSlide
};
